import { useEffect, useState } from "react";
import { toast } from "sonner";
import { acceleratorFromEvent, displayAccelerator, HOTKEY_LABELS } from "@/lib/hotkeys";

const ORDER = ["toggle", "move", "metar"];
const DEFAULTS = { toggle: "Control+Alt+H", move: "Control+Alt+M", metar: "Control+Alt+V" };
const btn = "text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border border-border hover:bg-secondary";

/**
 * The overlay's hotkeys, each changeable (2026-10-18): another app can already
 * own one (a tester's Ctrl+Alt+M), and then that key does nothing. A desktop
 * app older than this page cannot change them, and gets the fixed list.
 */
export default function OverlayHotkeys() {
  const api = window.electronAPI?.hotkeys;
  const [state, setState] = useState(null);
  const [capturing, setCapturing] = useState(null);   // action waiting for a key press

  useEffect(() => {
    let live = true;
    api?.get?.().then((s) => live && setState(s)).catch(() => {});
    return () => { live = false; };
  }, [api]);

  useEffect(() => {
    if (!capturing) return undefined;
    const onKey = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === "Escape") { setCapturing(null); return; }
      const accel = acceleratorFromEvent(e);
      if (!accel) return;   // a modifier on its own: wait for the key
      const action = capturing;
      setCapturing(null);
      api.set(action, accel).then((s) => {
        setState(s);
        if (s?.error) toast.error(s.error);
        else toast.success(`${HOTKEY_LABELS[action].split(":")[0]}: ${displayAccelerator(accel)}`);
      });
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturing, api]);

  const keys = state?.keys || DEFAULTS;
  const defaults = state?.defaults || DEFAULTS;
  const failed = new Set(state?.failed || []);

  return (
    <div className="mt-4 text-xs text-muted-foreground leading-relaxed" data-testid="settings-overlay-hotkeys">
      Works with the sim in front, and from the tray icon too:
      <div className="mt-2 grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 items-center">
        {ORDER.map((a) => (
          <div key={a} className="contents" data-testid={`hotkey-${a}`}>
            <kbd className={`font-mono ${failed.has(a) ? "text-red-700 dark:text-red-300 line-through" : "text-foreground"}`}>
              {capturing === a ? "Press keys…" : displayAccelerator(keys[a])}
            </kbd>
            <span>
              {HOTKEY_LABELS[a]}
              {failed.has(a) && capturing !== a && (
                <span className="block text-red-700 dark:text-red-300">
                  Not working: another app already uses this combination. Choose another.
                </span>
              )}
            </span>
            {state ? (
              <span className="flex gap-1.5 justify-end">
                {capturing === a ? (
                  <button type="button" className={btn} onClick={() => setCapturing(null)}>Cancel</button>
                ) : (
                  <button type="button" className={btn} onClick={() => setCapturing(a)}
                    data-testid={`hotkey-${a}-change`}>Change</button>
                )}
                {keys[a] !== defaults[a] && capturing !== a && (
                  <button type="button" className={btn}
                    onClick={() => api.set(a, null).then((s) => { setState(s); if (s?.error) toast.error(s.error); })}>
                    Default
                  </button>
                )}
              </span>
            ) : <span />}
          </div>
        ))}
      </div>
      {capturing && (
        <div className="mt-2">Hold Ctrl, Alt or Shift and press a letter, a digit or F1–F12. Esc cancels.</div>
      )}
    </div>
  );
}
